import { useEffect, useId, useRef, useState } from "react";
import ContextFacetFields, { emptyFacet } from "./ContextFacetFields";

const FACET_CONTEXTS = ["professional", "personal"];
const LABELS = { professional: "Professional", personal: "Personal" };

function normalizedContexts(enabledContexts, facets) {
  const requested = enabledContexts === "both"
    ? FACET_CONTEXTS
    : typeof enabledContexts === "string"
    ? [enabledContexts]
    : enabledContexts || FACET_CONTEXTS.filter((context) => facets?.[context]);
  const valid = FACET_CONTEXTS.filter((context) => requested?.includes(context));
  return valid.length > 0 ? valid : ["professional"];
}

function hasCustomDetails(facet, context) {
  if (!facet) return false;
  const defaults = emptyFacet(context);
  return (facet.relationshipType !== undefined && facet.relationshipType !== defaults.relationshipType)
    || (facet.strength !== undefined && facet.strength !== defaults.strength)
    || Boolean(facet.whereMet || facet.privateNote || facet.lastInteracted)
    || Boolean(facet.branch?.parentUid);
}

/** Copy relationship details without copying the destination's search consent. */
export function copyFacetDetails(source, target, targetContext) {
  const from = { ...emptyFacet(targetContext), ...(source || {}) };
  const destination = { ...emptyFacet(targetContext), ...(target || {}) };
  return {
    ...destination,
    relationshipType: from.relationshipType,
    strength: from.strength,
    whereMet: from.whereMet,
    privateNote: from.privateNote,
    lastInteracted: from.lastInteracted,
    branch: { ...emptyFacet(targetContext).branch, ...(from.branch || {}) },
    discoveryEnabled: destination.discoveryEnabled,
  };
}

/**
 * Tabs and fields for a person's enabled facets.
 *
 * `onChange(nextFacets, meta)` receives field edits and one-time copies.
 * Pass `onRemoveFacet(context)` to expose an edit-time removal action.
 */
export default function FacetEditor({
  relationship,
  facets: controlledFacets = {},
  enabledContexts: controlledEnabledContexts,
  onChange: controlledOnChange,
  activeContext: controlledActiveContext,
  onActiveContextChange: controlledOnActiveContextChange,
  networkNodes = [],
  currentUid,
  excludeIds,
  disabled: explicitlyDisabled = false,
  onRemoveFacet: controlledOnRemoveFacet,
  allowContextRemoval = false,
  canRemoveFacet = true,
  idPrefix,
  className = "",
}) {
  const facets = relationship?.facets || controlledFacets;
  const enabledContexts = relationship?.enabledContexts || controlledEnabledContexts;
  const onChange = relationship?.setFacets || controlledOnChange;
  const activeContext = relationship?.activeContext ?? controlledActiveContext;
  const onActiveContextChange = relationship?.selectContext || controlledOnActiveContextChange;
  const disabled = explicitlyDisabled || Boolean(relationship?.isSubmitting);
  const onRemoveFacet = controlledOnRemoveFacet || (
    allowContextRemoval ? relationship?.requestRemoval : undefined
  );
  const contexts = normalizedContexts(enabledContexts, facets);
  const [internalActive, setInternalActive] = useState(contexts[0]);
  const [copyConfirmation, setCopyConfirmation] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const generatedId = useId().replace(/:/g, "");
  const prefix = idPrefix || `facet-editor-${generatedId}`;
  const tabsRef = useRef([]);
  const requestedActive = activeContext ?? internalActive;
  const selected = contexts.includes(requestedActive) ? requestedActive : contexts[0];

  useEffect(() => {
    if (!contexts.includes(internalActive)) setInternalActive(contexts[0]);
  }, [contexts, internalActive]);

  const selectContext = (context, focus = false) => {
    if (disabled) return;
    if (activeContext === undefined) setInternalActive(context);
    onActiveContextChange?.(context);
    setCopyConfirmation(null);
    setCopyStatus("");
    if (focus) {
      const index = contexts.indexOf(context);
      tabsRef.current[index]?.focus();
    }
  };

  const handleTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % contexts.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + contexts.length) % contexts.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = contexts.length - 1;
    }
    if (nextIndex !== null) {
      event.preventDefault();
      selectContext(contexts[nextIndex], true);
    }
  };

  const updateSelected = (nextFacet) => {
    onChange?.(
      { ...facets, [selected]: nextFacet },
      { type: "field", context: selected },
    );
    setCopyStatus("");
  };

  const copyToOtherContext = () => {
    const target = contexts.find((context) => context !== selected);
    if (!target) return;

    if (copyConfirmation !== target && hasCustomDetails(facets[target], target)) {
      setCopyConfirmation(target);
      setCopyStatus("");
      return;
    }

    const nextFacet = copyFacetDetails(facets[selected], facets[target], target);
    onChange?.(
      { ...facets, [target]: nextFacet },
      { type: "copy", from: selected, to: target },
    );
    setCopyConfirmation(null);
    setCopyStatus(`Copied to ${LABELS[target]}. Future edits stay separate.`);
  };

  const removeAllowed = typeof canRemoveFacet === "function"
    ? canRemoveFacet(selected, contexts)
    : canRemoveFacet;
  const otherContext = contexts.find((context) => context !== selected);

  return (
    <section className={`facet-editor ${className}`.trim()} data-context={selected}>
      <div className="facet-editor-header">
        <div>
          <p className="eyebrow">Relationship details</p>
          <h4 className="facet-editor-title">Keep each side of the relationship distinct</h4>
        </div>
        {onRemoveFacet && removeAllowed && (
          <button
            type="button"
            className="facet-editor-remove"
            onClick={() => onRemoveFacet(selected)}
            disabled={disabled}
            aria-label={`Remove ${LABELS[selected]} facet`}
          >
            Remove {LABELS[selected]}
          </button>
        )}
      </div>

      {contexts.length > 1 && (
        <div className="facet-tabs" role="tablist" aria-label="Relationship context">
          {contexts.map((context, index) => (
            <button
              key={context}
              ref={(node) => { tabsRef.current[index] = node; }}
              type="button"
              id={`${prefix}-${context}-tab`}
              className={`facet-tab context-${context}${selected === context ? " is-active" : ""}`}
              role="tab"
              aria-selected={selected === context}
              aria-controls={`${prefix}-${context}-panel`}
              tabIndex={selected === context ? 0 : -1}
              onClick={() => selectContext(context)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              disabled={disabled}
            >
              <span className="facet-tab-mark" aria-hidden="true" />
              {LABELS[context]}
            </button>
          ))}
        </div>
      )}

      {otherContext && (
        <div className="facet-copy-bar">
          <div className="facet-copy-message">
            <span className="facet-copy-title">
              {copyConfirmation
                ? `Replace ${LABELS[otherContext]} details?`
                : `Reuse these details in ${LABELS[otherContext]}`}
            </span>
            <span>
              {copyConfirmation
                ? "Its discovery setting will stay unchanged."
                : "This copies once; the two sides will not stay linked."}
            </span>
          </div>
          <div className="facet-copy-actions">
            {copyConfirmation && (
              <button
                type="button"
                className="facet-copy-cancel"
                onClick={() => setCopyConfirmation(null)}
                disabled={disabled}
              >
                Keep existing
              </button>
            )}
            <button
              type="button"
              className="facet-copy-button"
              onClick={copyToOtherContext}
              disabled={disabled}
            >
              {copyConfirmation
                ? `Replace ${LABELS[otherContext]}`
                : `Copy to ${LABELS[otherContext]}`}
            </button>
          </div>
        </div>
      )}

      {copyStatus && <p className="facet-copy-status" role="status">{copyStatus}</p>}

      <div
        id={`${prefix}-${selected}-panel`}
        role={contexts.length > 1 ? "tabpanel" : undefined}
        aria-labelledby={contexts.length > 1 ? `${prefix}-${selected}-tab` : undefined}
        className="facet-editor-panel"
      >
        <ContextFacetFields
          context={selected}
          value={facets[selected]}
          onChange={updateSelected}
          networkNodes={networkNodes}
          currentUid={currentUid}
          excludeIds={excludeIds}
          disabled={disabled}
          idPrefix={`${prefix}-${selected}`}
        />
      </div>
    </section>
  );
}
